import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { useBannerContext } from "oolib";

import { getTenant } from "@/TENANT_CONFIGS";
import { getWebSocketEndpoint } from "@/webSocketDefaults.js";
import { useGetQueryData } from "@/utils/react-query-hooks/general";

/**
 * Per-document realtime collaboration. Manages a socket connection scoped to
 * a single published doc (`${tenant}-doc-${contentType}-${contentId}` room
 * on the server), tracks the per-annotation lock state for the doc, and
 * exposes acquire / release methods to descendants (AnnoLightboxDisplay,
 * AnnoPlugin handlers).
 *
 * Mirrors the standalone-socket pattern of useComponentCollaboration —
 * separate socket from the SocketContext one. Cheap (one extra connection
 * per open published-page tab) and keeps the collab plumbing decoupled from
 * the progress-events pipeline.
 *
 * Returns:
 *   isConnected:           boolean
 *   locks:                 { [annoDataId]: { userId, userName, avatar, socketId, sinceTimestamp } }
 *   acquireLock(id):       async () => { ok: boolean, lock?, reason? }
 *   releaseLock(id):       async () => void
 *   isLockedByMe(id):      boolean
 *   isLockedByOther(id):   boolean
 *   whoIsEditing(id):      { userName, avatar, userId } | null  (null if free or held by me)
 */
export const useDocCollaboration = ({ contentType, contentId, enabled = true }) => {
  const tenant = getTenant();
  const userData = useGetQueryData("userData");
  const currentUser = userData?.user;

  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [locks, setLocks] = useState({});
  // Latest socket id is captured here for acquireLock-acks that arrive
  // asynchronously, since `socket.id` reads off the current ref directly.
  const socketRef = useRef(null);

  // oolib BannerProvider must be mounted up the tree (it is, via App-level
  // providers). doc-content-changed events trigger an info banner with a
  // Refresh CTA — keyed by contentId so repeated events from peers replace
  // each other rather than stacking.
  const { SET_INFO_BANNER } = useBannerContext();

  const SOCKET_IO_ENDPOINT = getWebSocketEndpoint();

  useEffect(() => {
    if (
      !enabled ||
      !currentUser ||
      !tenant ||
      !contentType ||
      !contentId
    ) {
      return undefined;
    }

    const socketInstance = io(SOCKET_IO_ENDPOINT, { transports: ["polling"] });
    socketRef.current = socketInstance;

    socketInstance.on("connect", () => {
      setIsConnected(true);
      setSocket(socketInstance);
      socketInstance.emit("join-doc", {
        tenant,
        contentType,
        contentId,
        user: currentUser,
      });
    });

    socketInstance.on("disconnect", () => {
      setIsConnected(false);
      setLocks({});
    });

    socketInstance.on("anno-locks-updated", (data) => {
      // Server scopes the broadcast to the doc room, but be defensive — only
      // accept updates targeted at this hook's room.
      if (!data) return;
      setLocks(data.locks || {});
    });

    // Fired by the server when ANOTHER user on this doc successfully saves
    // (create / edit / delete). The server already excludes the sender, so
    // we only see events from peers. We surface it as an oolib info banner
    // with a Refresh CTA — we deliberately don't auto-refetch because the
    // LexicalTextEditor is uncontrolled and won't pick up the new content
    // without a remount.
    //
    // Fixed banner id (`doc-updated-${contentId}`) so rapid repeat events
    // from the same doc replace the existing banner rather than stacking.
    socketInstance.on("doc-content-changed", (data) => {
      if (!data) return;
      const byName = data.byUser?.name;
      SET_INFO_BANNER({
        id: `doc-updated-${contentId}`,
        msg: byName
          ? `${byName} updated annotations on this page. Refresh to see the latest.`
          : "This page has been updated. Refresh to see the latest.",
        color: "yellow",
        cta: {
          text: "Refresh",
          action: () => window.location.reload(),
        },
      });
    });

    return () => {
      try {
        socketInstance.emit("leave-doc", { tenant, contentType, contentId });
      } catch (e) {
        // Socket may already be closed; nothing to do.
      }
      socketInstance.disconnect();
      socketRef.current = null;
    };
  }, [enabled, currentUser?._id, tenant, contentType, contentId]);

  const acquireLock = useCallback(
    (annoDataId) => {
      return new Promise((resolve) => {
        const s = socketRef.current;
        if (!s || !currentUser) {
          resolve({ ok: false, reason: "not-connected" });
          return;
        }
        s.emit(
          "acquire-anno-lock",
          { tenant, contentType, contentId, annoDataId, user: currentUser },
          (response) => {
            resolve(response || { ok: false, reason: "no-ack" });
          }
        );
      });
    },
    [tenant, contentType, contentId, currentUser?._id]
  );

  const releaseLock = useCallback(
    (annoDataId) => {
      return new Promise((resolve) => {
        const s = socketRef.current;
        if (!s) {
          resolve();
          return;
        }
        s.emit(
          "release-anno-lock",
          { tenant, contentType, contentId, annoDataId },
          () => resolve()
        );
      });
    },
    [tenant, contentType, contentId]
  );

  // Notify peers in the doc room that this user just saved an annotation
  // change. Server fans out to everyone except this socket.
  const emitContentChange = useCallback(
    (kind = "annotation-changed") => {
      const s = socketRef.current;
      if (!s || !currentUser) return;
      s.emit("doc-content-changed", {
        tenant,
        contentType,
        contentId,
        byUser: { _id: currentUser._id, name: currentUser.name },
        kind,
      });
    },
    [tenant, contentType, contentId, currentUser?._id, currentUser?.name]
  );


  const helpers = useMemo(() => {
    const mySocketId = socket?.id;
    return {
      // Strictly socketId-based — matches the server's authority model
      // (server rejects acquire attempts from any socket other than the
      // current holder, even if same user). This means the same user
      // opening a second tab is treated as a separate "editor" and the
      // second tab will show the lock indicator. Correct: if Tab 1 is
      // mid-edit, Tab 2 shouldn't be allowed to start a competing edit
      // on the same annotation.
      isLockedByMe: (annoDataId) => locks[annoDataId]?.socketId === mySocketId,
      isLockedByOther: (annoDataId) => {
        const lock = locks[annoDataId];
        return !!lock && lock.socketId !== mySocketId;
      },
      whoIsEditing: (annoDataId) => {
        const lock = locks[annoDataId];
        if (!lock || lock.socketId === mySocketId) return null;
        return {
          userId: lock.userId,
          userName: lock.userName,
          avatar: lock.avatar,
        };
      },
    };
  }, [locks, socket?.id]);

  return {
    isConnected,
    locks,
    acquireLock,
    releaseLock,
    emitContentChange,
    ...helpers,
  };
};
